"use client";

import React from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { useLocale, useLocalizedText } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import { servicesList } from "@/lib/data";
import { getServiceRoute } from "@/lib/serviceUtils";
import { cn } from "@/lib/utils";

export interface ServicePageLayoutProps {
  title: string;
  breadcrumb: string;
  currentServiceId: string;
  children: React.ReactNode;
  /** Substring of the title that receives the editorial underline; overrides the trailing-words heuristic. */
  accent?: string;
  /** Short supporting line rendered under the H1. */
  dek?: string;
  /**
   * Hero CTA label. Undefined renders the default "Request a Consultation";
   * pass `false` to hide the CTA entirely.
   */
  ctaLabel?: string | false;
  /** Hero CTA destination; defaults to "/contact/" (localized). */
  ctaHref?: string;
  /** Extra hero actions rendered beside the CTA (e.g. a WhatsAppCta). */
  heroActions?: React.ReactNode;
}

export default function ServicePageLayout({
  title,
  breadcrumb,
  currentServiceId,
  children,
  accent,
  dek,
  ctaLabel,
  ctaHref,
  heroActions
}: ServicePageLayoutProps) {
  const prefersReducedMotion = useReducedMotion();
  const locale = useLocale();
  const activePillRef = React.useRef<HTMLAnchorElement>(null);

  // Keep the current page's pill visible inside the hidden-scrollbar strip.
  React.useEffect(() => {
    activePillRef.current?.scrollIntoView({
      inline: "center",
      block: "nearest",
    });
  }, []);
  const translatedTitle = useLocalizedText(title);
  const translatedBreadcrumb = useLocalizedText(breadcrumb);
  const translatedAccent = useLocalizedText(accent ?? "");
  const translatedDek = useLocalizedText(dek ?? "");

  // Explicit accent overrides the trailing-words heuristic when it matches the title.
  const accentIndex = translatedAccent
    ? translatedTitle.indexOf(translatedAccent)
    : -1;

  let leading: string;
  let accentText: string;
  let trailing = "";

  if (accentIndex !== -1) {
    leading = translatedTitle.slice(0, accentIndex).trimEnd();
    accentText = translatedAccent;
    trailing = translatedTitle.slice(accentIndex + translatedAccent.length);
  } else {
    // Split the headline so the trailing words carry the editorial underline.
    const words = translatedTitle.split(" ");
    const splitIndex =
      words.length > 3 ? words.length - 2 : words.length > 1 ? words.length - 1 : 0;
    leading = words.slice(0, splitIndex).join(" ");
    accentText = words.slice(splitIndex).join(" ");
  }

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.7, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  return (
    <div className="relative bg-background">
      {/* Hero band */}
      <section className="relative">
        <div className="relative mx-auto max-w-7xl px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pb-12 lg:pt-28">
          <motion.p
            {...reveal(0)}
            className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
          >
            {translatedBreadcrumb}
          </motion.p>
          <motion.h1
            {...reveal(0.1)}
            className="max-w-4xl font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-6xl"
          >
            {leading ? <>{leading} </> : null}
            <span className="emphasis-underline">{accentText}</span>
            {trailing ? <>{trailing}</> : null}
          </motion.h1>
          {dek ? (
            <motion.p
              {...reveal(0.18)}
              className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-muted"
            >
              {translatedDek}
            </motion.p>
          ) : null}
          {ctaLabel !== false ? (
            <motion.div
              {...reveal(0.24)}
              className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center"
            >
              <Link
                href={getLocalizedPathname(ctaHref ?? "/contact/", locale)}
                data-track-id="service-hero-cta"
                className="inline-flex w-full items-center justify-center rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-emphasis sm:w-auto"
              >
                {t(locale, ctaLabel ?? "Request a Consultation")}
              </Link>
              {heroActions}
            </motion.div>
          ) : null}
        </div>

        {/* Horizontal service switcher — replaces the old sidebar */}
        <motion.nav
          {...reveal(0.2)}
          aria-label={t(locale, "Our Services")}
          className="relative border-b border-border"
        >
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <ul className="-mb-px -mx-4 flex gap-1 overflow-x-auto scroll-px-4 px-4 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {servicesList.map((service) => {
                const isActive = service.detailId === currentServiceId;
                return (
                  <li key={service.detailId} className="shrink-0">
                    <Link
                      href={getLocalizedPathname(
                        getServiceRoute(service.detailId),
                        locale,
                      )}
                      aria-current={isActive ? "page" : undefined}
                      ref={isActive ? activePillRef : undefined}
                      className={cn(
                        "relative inline-flex whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-neutral-emphasis/60 hover:bg-neutral-surface hover:text-neutral-emphasis",
                      )}
                    >
                      {t(locale, service.title)}
                    </Link>
                  </li>
                );
              })}
            </ul>
            {/* Edge fades signalling horizontal scrollability */}
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-background to-transparent"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-background to-transparent"
              aria-hidden
            />
          </div>
        </motion.nav>
      </section>

      {/* Content area — full width */}
      <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-14 sm:px-6 lg:px-8 lg:pb-28 lg:pt-16">
        {children}
      </div>
    </div>
  );
}
